import { Box, Typography } from "@mui/material";

import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  AnalyticsHeader,
  HoustonAstrosCheckoutTerms,
  HoustonAstrosAdditionalDetailsDisclaimerTC,
} from "./helpers";
import { vendorUUIDs } from "../../constants";
import { HoustonAstrosTCListItems } from "../tc_helper_houston";
import { DefaultRegistrationTerms } from "../../default_terms_and_conditions";

import Banner from "public/images/houstonastros/Marketing-Banner-Image-1486x148.jpg";
import BackgroundImage from "public/images/houstonastros/background.jpg";

import type { TeamConfig } from "../types";

const LogoImage = "https://ss-leagues.s3.amazonaws.com/MLB-3/HOU-3.png";

const config: TeamConfig = {
  teamSchema: "houstonastros_suites",
  browserTitle: "Houston Astros",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  mlbTeamId: 117,
  teamName: "Houston Astros",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10178-astros-sec",
    mapId: "blockmap2d",

    suiteImages: [],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SUITE-LEVEL-V2",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    hideViewTicketsInThankYouPage: true,

    isCheckoutAdditionalDetailsEnabled: true,
    isPerksEnabled: false,
    isCartCacheEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    googleTagManagerId: "",
    AnalyticsHeader: AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://bp.mlb.com/Wallet",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",
  shouldUseInternalLogin: false,
  useFirstAndLastNames: false,

  isBrainTreePaymentEnabled: false,

  authConfigs: {
    isMlbBallparkSSOEnabled: true,
  },

  // images
  images: {
    logoNameImage: "",
    seatMap3dPreviewImage: "",
    faviconImage: LogoImage,
    giftCardStandalonePageImage: "",
    findGamesBannerImage: Banner.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    hidePackageBalanceOnCheckout: true,
    hideServiceFee: true,

    inventoryCardSettings: {
      isPBLineItemsVisible: true,
      seatCountLabel: "Tickets Included",
      inventorySuitePackageInfo: "$600",
      pbLineItemsValue: 5,
      inventoryCardFooterText:
        "Estimated price; final balance shown at Checkout.",
      hideFBPackageLineItem: true,
    },
    inventoryContainer: {
      getGridCellHeight: (arg) => (arg?.mdDown ? 260 : 180),
    },

    hamburgerMenu: {
      logoutButtonEnabled: false,
      hideLoginButton: true,
      disclaimer: (
        <Box maxWidth={350} px={2}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            © {new Date().getFullYear()} MLB Advanced Media, L.P. MLB trademarks and copyrights are used
            with permission of Major League Baseball. Any other marks used herein
            are trademarks of their respective owners.
          </Typography>
        </Box>
      ),
    },

    authPage: {
      hideWrongAccountOption: true,
      hideAccountDetails: true,
    },

    checkoutPage: {
      checkoutForm: {
        optionalAdditionalDetailsContractTitle: true,
        optionalAdditionalDetailsContractEmail: true,
        optionalAdditionalDetailsCompanyName: true,
        optionalAdditionalDetailsContractFirstName: true,
        optionalAdditionalDetailsContractLastName: true,
        optionalAdditionalDetailsContractPhoneNumber: true,
        optionalAdditionalDetailsCateringName: true,
        optionalAdditionalDetailsCateringPhone: true,
        optionalAdditionalDetailsCateringEmail: true,
        optionalAdditionalDetailsDisclaimerFullName: true,

        termsAndConditionsDefaultChecked: true,
      },
    },

    purchaseHistory: {
      hideAccessTicketButton: true,
    },
  },

  colors: {
    primary: "#002D62",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    registerPageLanguage:
      "Please enter your phone number to receive notifications regarding your purchase.",
    topNavMenuItems: <HoustonAstrosTCListItems />,
    checkoutTermsAndCondition: <HoustonAstrosCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    additionalDetailsDisclaimerTC: (
      <HoustonAstrosAdditionalDetailsDisclaimerTC />
    ),
    purchaseSeats: {
      progressBarSuccessText:
        "You're good to go! Add more games or select Continue to view seating options.",
      progressBarAditionalText: " to continue!",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,
    optionsMenuLoginButtonLanguage: "Continue without Selecting a Game",

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutServiceNote:
      "Your reservation is not yet complete until notification from the Houston Astros confirming your reservation has been accepted in accordance with our agreement.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
